// W38 / M38 — Irányítópult.
//
// A belépés utáni első képernyő: négy statisztika-csempe, alattuk rövid listák
// — mai feladatok, lejáró határidők, elfogadásra várók — és a csapat terhelése.
// Minden érték a már betöltött feladatokból számolódik, külön lekérdezés nélkül;
// a szűrő-logika ezért tiszta, adatbázis nélkül is tesztelhető függvényekben él.

import { t } from '../lib/i18n';
import { avatar, emptyState } from '../lib/components';
import { h, icon } from '../lib/dom';
import { denseTaskTable } from './tasks';
import type { PersonView } from '../lib/data';
import { formatElapsed, type TaskView } from '../lib/model';

const clockPath = 'M8 3.2a4.8 4.8 0 100 9.6 4.8 4.8 0 000-9.6M8 5.6V8l1.6 1';

const DAY_MS = 24 * 60 * 60 * 1000;

/// Nyitott a feladat, amíg nincs lezárva és nincs elutasítva.
export function isOpenTask(task: TaskView): boolean {
  return task.status !== 'completed' && task.status !== 'failed' && task.status !== 'unaccepted';
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/// A mai teendők: ma lejáró, már lejárt, vagy éppen mért nyitott feladatok.
///
/// A lejártakat szándékosan idevesszük — reggel azokkal kell először kezdeni.
export function todaysTasks(tasks: TaskView[], now: Date): TaskView[] {
  return tasks
    .filter(
      (task) =>
        isOpenTask(task) &&
        ((task.deadline !== undefined && isSameDay(task.deadline, now)) ||
          task.isOverdue ||
          task.timerRunning),
    )
    .sort(deadlineFirst);
}

/// Határidő szerint növekvő; a határidő nélküli feladat a lista végére kerül.
function deadlineFirst(a: TaskView, b: TaskView): number {
  const left = a.deadline?.getTime() ?? Number.POSITIVE_INFINITY;
  const right = b.deadline?.getTime() ?? Number.POSITIVE_INFINITY;
  return left - right;
}

/// A legközelebbi határidők — a lejártak elöl.
export function upcomingDeadlines(tasks: TaskView[], limit = 5): TaskView[] {
  return tasks
    .filter((task) => isOpenTask(task) && task.deadline !== undefined)
    .sort(deadlineFirst)
    .slice(0, limit);
}

/// 24 órán belül lejáró (vagy már lejárt) nyitott feladatok.
export function dueWithin24Hours(tasks: TaskView[], now: Date): TaskView[] {
  const edge = now.getTime() + DAY_MS;
  return tasks.filter(
    (task) => isOpenTask(task) && task.deadline !== undefined && task.deadline.getTime() <= edge,
  );
}

export function waitingForAcceptance(tasks: TaskView[]): TaskView[] {
  return tasks.filter((task) => task.status === 'waitingToAccept');
}

export type TeamLoadRow = { person: PersonView; openCount: number };

/// Ki hány nyitott feladatot visz — csökkenő sorrendben.
export function teamLoad(tasks: TaskView[], people: PersonView[]): TeamLoadRow[] {
  const open = tasks.filter(isOpenTask);

  return people
    .map((person) => ({
      person,
      openCount: open.filter((task) => task.assigneeIds.includes(person.id)).length,
    }))
    .sort(
      (a, b) =>
        b.openCount - a.openCount || a.person.fullName.localeCompare(b.person.fullName, 'hu'),
    );
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/// Emberi határidő-felirat: „Ma 16:00”, „Holnap 09:00”, „2 napja lejárt”, „09. 30.”
export function relativeDeadline(deadline: Date, now: Date): string {
  const time = `${pad(deadline.getHours())}:${pad(deadline.getMinutes())}`;
  if (isSameDay(deadline, now)) return `Ma ${time}`;

  const tomorrow = new Date(now.getTime() + DAY_MS);
  if (isSameDay(deadline, tomorrow)) return `Holnap ${time}`;

  // Naptári napokban számolunk, nem 24 órás blokkokban: a tegnap 23:00 is
  // „1 napja lejárt”, nem „ma”.
  const startOfNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDeadline = new Date(
    deadline.getFullYear(),
    deadline.getMonth(),
    deadline.getDate(),
  ).getTime();
  const days = Math.round((startOfNow - startOfDeadline) / DAY_MS);
  if (days > 0) return t('dashboard.overdueDays', [String(days)]);

  return `${pad(deadline.getMonth() + 1)}. ${pad(deadline.getDate())}.`;
}

function greeting(now: Date, userName?: string): string {
  const hour = now.getHours();
  const part =
    hour < 10
      ? t('dashboard.goodMorning')
      : hour < 18
        ? t('dashboard.goodDay')
        : t('dashboard.goodEvening');
  return userName ? `${part}, ${userName}!` : `${part}!`;
}

function nameOf(people: PersonView[], id: string): string {
  return people.find((person) => person.id === id)?.fullName ?? 'Ismeretlen';
}

/// A terv mérőszám-kártyája: címke, 30 px-es mono érték, magyarázó sor.
///
/// A `tone` csak akkor szín, ha a szám maga figyelmeztet (lejáró határidő,
/// elfogadásra váró feladat) — a magyarázat akkor is ott áll mellette, tehát
/// nem csak a szín hordozza a jelentést.
function statTile(label: string, value: string, hint: string, tone?: string): HTMLElement {
  return h(
    'div',
    { class: 'card pad16 col', style: { gap: '4px', minWidth: '0' } },
    h('span', { class: 'label', text: label }),
    h('span', {
      class: 'mono w7',
      style: { fontSize: '30px', lineHeight: '1.1', color: tone ?? 'var(--tp)' },
      text: value,
    }),
    h('span', { class: 'caption', text: hint }),
  );
}

/// Panel fejléccel; a fejlécben elfér egy darabszám-jelvény és egy művelet.
function panel(
  title: string,
  extras: { badge?: HTMLElement | null; action?: HTMLElement | null },
  ...rows: (HTMLElement | null)[]
): HTMLElement {
  return h(
    'section',
    { class: 'panel', style: { overflow: 'hidden' } },
    h(
      'div',
      {
        class: 'row g6',
        style: { padding: '12px 16px', borderBottom: '1px solid var(--border)' },
      },
      h('h3', { class: 'h3', text: title }),
      extras.badge ?? null,
      h('span', { class: 'grow' }),
      extras.action ?? null,
    ),
    ...rows,
  );
}

/// Kattintható listasor. Valódi gomb, hogy billentyűzetről is elérhető legyen.
function rowButton(onClick: () => void, ...children: (HTMLElement | null)[]): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: 'lrow',
      style: {
        width: '100%',
        border: '0',
        borderBottom: '1px solid var(--border)',
        textAlign: 'left',
        font: 'inherit',
        cursor: 'pointer',
      },
      onClick,
    },
    ...children,
  );
}

function emptyRow(text: string): HTMLElement {
  return h('p', { class: 'caption', style: { padding: '16px' }, text });
}

function titleBlock(task: TaskView): HTMLElement {
  const meta = [task.projectTitle, task.sequenceNumber ? `#${task.sequenceNumber}` : undefined]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return h(
    'span',
    { class: 'col grow', style: { gap: '2px', minWidth: '0' } },
    h('span', { class: 'body-sm w6', text: task.title }),
    h('span', { class: 'caption', text: meta.length > 0 ? meta : t('dashboard.standaloneTask') }),
  );
}

function deadlineText(task: TaskView, now: Date): HTMLElement {
  return h('span', {
    class: task.isOverdue ? 'caption overdue' : 'caption',
    style: { width: '120px', flex: 'none', textAlign: 'right' },
    text: task.deadline ? relativeDeadline(task.deadline, now) : t('dashboard.noDeadline'),
  });
}

export type DashboardPageOptions = {
  tasks: TaskView[];
  people: PersonView[];
  onTaskSelected: (task: TaskView) => void;
  /// Az üdvözléshez; ha hiányzik, csak a napszak jelenik meg.
  userName?: string;
  /// Rögzíthető „most” — enélkül a mai/lejáró számok tesztelhetetlenek lennének.
  now?: Date;
  /// A mai feladatok panel „Összes” hivatkozása; ha nincs, a link elmarad.
  onShowAllTasks?: () => void;
};

export function dashboardPage(options: DashboardPageOptions): HTMLElement {
  const now = options.now ?? new Date();
  const { tasks, people } = options;

  const today = todaysTasks(tasks, now);
  const due = dueWithin24Hours(tasks, now);
  const overdue = tasks.filter((task) => task.isOverdue);
  const waiting = waitingForAcceptance(tasks);
  const load = teamLoad(tasks, people);
  const deadlines = upcomingDeadlines(tasks);

  const doingCount = today.filter((task) => task.status === 'doing').length;
  const runningCount = tasks.filter((task) => task.timerRunning).length;
  const measuredToday = today.reduce((sum, task) => sum + task.elapsedSeconds, 0);
  const waitingNames = [...new Set(waiting.flatMap((task) => task.assigneeIds))]
    .slice(0, 2)
    .map((id) => nameOf(people, id))
    .join(', ');

  const head = h(
    'div',
    { class: 'col g4' },
    h('h2', { class: 'h2', text: greeting(now, options.userName) }),
    h('span', {
      class: 'caption',
      text: now.toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
      }),
    }),
  );

  const tiles = h(
    'div',
    { class: 'grid c4' },
    statTile('Mai feladatok', String(today.length), `${doingCount} folyamatban`),
    statTile(
      t('dashboard.dueIn24h'),
      String(due.length),
      t('dashboard.alreadyOverdue', [String(overdue.length)]),
      due.length > 0 || overdue.length > 0 ? 'var(--warn)' : undefined,
    ),
    statTile(
      t('task.waitingToAccept'),
      String(waiting.length),
      waiting.length === 0 ? 'Mindenki elfogadta' : waitingNames,
      waiting.length > 0 ? 'var(--warn)' : undefined,
    ),
    statTile(
      t('dashboard.measuredToday'),
      formatElapsed(measuredToday),
      t('dashboard.timersRunning', [String(runningCount)]),
    ),
  );

  const todayPanel = panel(
    'Mai feladatok',
    {
      action: options.onShowAllTasks
        ? h('button', {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            text: t('dashboard.showAll'),
            onClick: options.onShowAllTasks,
          })
        : null,
    },
    // A terv UGYANAZT a suru feladatsort mutatja, mint a feladatlista (W05)
    // es a projekt reszletei (W20) — kozos komponens, hogy az oszlopok ne
    // csuszhassanak szet a harom kepernyo kozott.
    today.length === 0
      ? emptyRow(t('dashboard.nothingDueToday'))
      : // Vizszintesen gorgetheto burok: a ketoszlopos iranyitopulton ez a
        // panel keskenyebb, mint amit a hat oszlop igenyel, es az utolso
        // oszlop (felelos) levagodott. Oszlopot elrejteni rosszabb lenne —
        // az informacio eltunne; igy csak gorgetni kell erte.
        h(
          'div',
          { class: 'table-scroll-x' },
          denseTaskTable({
            tasks: today,
            people,
            onSelect: options.onTaskSelected,
          }),
        ),
  );

  const deadlinePanel = panel(
    t('dashboard.upcomingDeadlines'),
    {},
    ...(deadlines.length === 0
      ? [emptyRow(t('dashboard.noOpenWithDeadline'))]
      : deadlines.map((task) =>
          rowButton(
            () => options.onTaskSelected(task),
            h(
              'span',
              { class: 'ic', style: { color: task.isOverdue ? 'var(--err)' : 'var(--ts)' } },
              icon(clockPath),
            ),
            titleBlock(task),
            deadlineText(task, now),
          ),
        )),
  );

  const maxLoad = load.reduce((max, row) => Math.max(max, row.openCount), 0);

  const loadPanel = h(
    'section',
    { class: 'panel pad20 col g12' },
    h('h3', { class: 'h3', text: t('dashboard.teamLoad') }),
    ...(load.length === 0
      ? [h('p', { class: 'caption', text: t('dashboard.noUsersToShow') })]
      : load.map((row) => {
          const percent = maxLoad === 0 ? 0 : Math.round((row.openCount / maxLoad) * 100);
          // Öt nyitott feladat fölött már érdemes átosztani — ezt a sáv színe is jelzi,
          // de a darabszám mindig ott áll mellette, tehát nem csak szín hordozza.
          const tone =
            row.openCount >= 5 ? 'var(--err)' : row.openCount >= 4 ? 'var(--warn)' : 'var(--accent)';

          return h(
            'div',
            { class: 'row g12' },
            avatar(row.person.fullName, 'sm'),
            h('span', {
              class: 'body-sm',
              style: { width: '120px', flex: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              title: row.person.fullName,
              text: row.person.fullName,
            }),
            h(
              'div',
              {
                class: 'prog grow',
                role: 'progressbar',
                'aria-valuenow': String(row.openCount),
                'aria-valuemin': '0',
                'aria-valuemax': String(maxLoad),
                'aria-label': `${row.person.fullName}: ${row.openCount} nyitott feladat`,
              },
              h('i', { style: { width: `${percent}%`, background: tone } }),
            ),
            h('span', {
              class: 'caption mono',
              style: { width: '72px', flex: 'none', textAlign: 'right' },
              text: `${row.openCount} nyitott`,
            }),
          );
        })),
    h('span', {
      class: 'caption',
      text: t('dashboard.loadBarHint'),
    }),
  );

  const waitingPanel = panel(
    t('task.waitingToAccept'),
    { badge: h('span', { class: 'badge badge-sq st-wait', text: String(waiting.length) }) },
    ...(waiting.length === 0
      ? [emptyRow(t('dashboard.nothingWaitingAccept'))]
      : waiting.map((task) =>
          rowButton(
            () => options.onTaskSelected(task),
            avatar(task.assigneeIds[0] ? nameOf(people, task.assigneeIds[0]) : '?', 'sm'),
            h(
              'span',
              { class: 'col grow', style: { gap: '2px', minWidth: '0' } },
              h('span', { class: 'body-sm w6', text: task.title }),
              h('span', {
                class: 'caption',
                text:
                  task.assigneeIds.map((id) => nameOf(people, id)).join(', ') ||
                  t('dashboard.noAssignee'),
              }),
            ),
            task.deadline ? deadlineText(task, now) : null,
          ),
        )),
  );

  if (tasks.length === 0) {
    return h(
      'div',
      { class: 'col g24 content-wide', style: { padding: '20px 24px' } },
      head,
      emptyState({
        title: t('dashboard.noTaskYetTitle'),
        text: t('dashboard.noTaskYetText'),
      }),
    );
  }

  return h(
    'div',
    { class: 'col g24 content-wide', style: { padding: '20px 24px' } },
    head,
    tiles,
    h(
      'div',
      { class: 'row g16 wrap', style: { alignItems: 'flex-start' } },
      h(
        'div',
        // A bal oszlop SZANDEKOSAN nagyobb reszt kap. A suru feladatsor fix
        // oszlopai (azonosito, prioritas, statusz, hatarido, felelos, egyeb)
        // onmagukban 722 px-t kernek a kozokkel egyutt — az eddigi 520 px-es
        // alapnal a panel ennel SZUKEBB lett, es az utolso oszlop egyszeruen
        // levagodott. Merve: 686 px-es panel, 722 px-es igeny.
        //
        // Az alap 1050 px, es ez SZANDEKOS: a sor tordel (`wrap`), tehat a ket
        // oszlop csak akkor all egymas melle, ha a bal tenyleg elfer. 760-nal
        // 1600 px-es ablakon meg egymas melle fertek, de a panel 854 px-re
        // szorult es a cimoszlopra 130 px maradt — rosszabb, mint 1440-en,
        // ahol mar egymas ala kerultek. Merve, nem becsulve.
        { class: 'col g16 grow', style: { minWidth: '0', flexBasis: '1050px', flexGrow: '3' } },
        todayPanel,
        deadlinePanel,
      ),
      h(
        'div',
        // A terv 400 px-es jobb oszlopot mutat; keskeny kepernyon a `wrap`
        // miatt a bal oszlop ala kerul.
        { class: 'col g16', style: { width: '400px', flexGrow: '1', minWidth: '0', maxWidth: '460px' } },
        loadPanel,
        waitingPanel,
      ),
    ),
  );
}
